import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { hurricaneApi } from '../api';
import { hurricaneReportView } from '../../../constants/routes';

function HurricaneReports() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    hurricaneApi.properties().then((res) => setProperties(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await hurricaneApi.reports({
          propertyId: propertyId || undefined,
        });
        if (!cancel) setRows(res.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.error || e.message || 'Failed to load reports');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [propertyId]);

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <div className="app-shell-page">
      <div className="app-page-header mb-4">
        <h2 className="h5 mb-1">Reports</h2>
        <p className="text-muted small mb-0">Filter hurricane damage reports by community.</p>
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card app-card bg-white mb-4">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label small mb-0">Community</label>
              <select
                className="form-select form-select-sm"
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
              >
                <option value="">All</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="app-loading text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          <span>Loading…</span>
        </div>
      ) : (
        <motion.div
          key={`hurricane-rep-${propertyId}`}
          initial={reduceMotion ? false : { opacity: 0.9, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="card app-card bg-white">
            <div className="card-body p-0">
              <div className="app-table-wrap table-responsive">
                <table className="table table-striped table-hover table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Community</th>
                      <th>Site</th>
                      <th>Hurricane</th>
                      <th>Outcome</th>
                      <th>Damage lines</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4 small app-empty-hint">
                          No reports found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr
                          key={r.id}
                          role="button"
                          tabIndex={0}
                          className="rv-report-row"
                          onClick={() => navigate(hurricaneReportView(r.id))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(hurricaneReportView(r.id));
                            }
                          }}
                        >
                          <td>{fmt(r.inspected_at)}</td>
                          <td>{r.property_name}</td>
                          <td>{r.location_name || '—'}</td>
                          <td>{r.hurricane_name}</td>
                          <td>{r.outcome || '—'}</td>
                          <td>{r.damage_count}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(hurricaneReportView(r.id));
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default HurricaneReports;
